import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { 
  Plus, 
  Search, 
  X,
  Edit,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  Upload,
  Package,
  AlertTriangle,
  Loader2,
  Eye
} from 'lucide-react';
import { inventoryAPI } from '@shared/apiClient';
import InventoryFormModal from '../components/InventoryFormModal';
import InventoryDetailsModal from '../components/InventoryDetailsModal';

export default function Inventory() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('itemNumber');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'active', 'inactive', 'lowStock'
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formMode, setFormMode] = useState('create');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedItemForDetails, setSelectedItemForDetails] = useState(null);
  const [highlightedItemNumber, setHighlightedItemNumber] = useState(null);

  // Build query params
  const queryParams = {
    page,
    limit: pageSize,
    sortBy: sortField,
    sortOrder: sortDirection,
    ...(searchTerm && { search: searchTerm }),
    ...(activeFilter === 'active' && { isActive: true }),
    ...(activeFilter === 'inactive' && { isActive: false }),
    ...(activeFilter === 'lowStock' && { lowStock: true })
  };

  // Fetch inventory items
  const { data, isLoading } = useQuery(
    ['inventory', queryParams],
    () => inventoryAPI.getInventory(queryParams),
    { keepPreviousData: true }
  );

  const pagination = data?.data?.pagination;
  const inventory = data?.data?.inventory || [];

  // Delete mutation
  const deleteMutation = useMutation(
    (id) => inventoryAPI.deleteInventoryItem(id),
    {
      onSuccess: () => {
        toast.success('Inventory item deleted successfully');
        queryClient.invalidateQueries('inventory');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete inventory item');
      }
    }
  );

  // Import mutation
  const importMutation = useMutation(
    (file) => inventoryAPI.importInventory(file),
    {
      onSuccess: (response) => {
        const results = response.data.results;
        toast.success(
          `Import completed: ${results.created} created, ${results.updated} updated, ${results.skipped} skipped`
        );
        if (results.errors.length > 0) {
          console.error('Import errors:', results.errors);
          toast.error(`${results.errors.length} errors occurred during import`);
        }
        queryClient.invalidateQueries('inventory');
        setIsImportModalOpen(false);
        setImportFile(null);
        setIsImporting(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to import inventory');
        setIsImporting(false);
      }
    }
  );

  const handleCreateClick = () => {
    setSelectedItem(null);
    setFormMode('create');
    setIsFormModalOpen(true);
  };

  const handleItemCreated = (createdItem) => {
    setSearchTerm(createdItem.itemNumber);
    setPage(1);
    setHighlightedItemNumber(createdItem.itemNumber);
    setTimeout(() => setHighlightedItemNumber(null), 5000);
  };

  const handleEditClick = (item) => {
    setSelectedItem(item);
    setFormMode('edit');
    setIsFormModalOpen(true);
  };

  const handleDeleteClick = (item) => {
    if (window.confirm(`Are you sure you want to delete "${item.itemNumber} - ${item.description}"?`)) {
      deleteMutation.mutate(item.id);
    }
  };

  const handleViewClick = (item) => {
    setSelectedItemForDetails(item);
    setIsDetailsModalOpen(true);
  };

  const handleImportClick = () => {
    setIsImportModalOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setImportFile(file);
    }
  };

  const handleImportSubmit = () => {
    if (!importFile) {
      toast.error('Please select a file');
      return;
    }
    setIsImporting(true);
    importMutation.mutate(importFile);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-blue-600" />
      : <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  // Check for low stock items
  const lowStockItems = useMemo(() => {
    return inventory.filter(item => 
      item.reorderPoint !== null && 
      parseFloat(item.quantityOnHand) <= parseFloat(item.reorderPoint)
    );
  }, [inventory]);

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="hub-page-header flex justify-between items-center">
        <div>
          <h1>Inventory</h1>
          <p>Manage inventory parts and materials ({pagination?.total || 0} items)</p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={handleImportClick}
            className="btn btn-secondary"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </button>
          <button 
            onClick={handleCreateClick}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4">

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
            <span className="text-sm font-medium text-yellow-800">
              {lowStockItems.length} item{lowStockItems.length === 1 ? '' : 's'} below reorder point
            </span>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1 relative mr-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by item number, description, or MPN..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          {/* Filter Buttons */}
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-700">Filter:</span>
            <button
              onClick={() => {
                setActiveFilter('all');
                setPage(1);
              }}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeFilter === 'all'
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Items
            </button>
            <button
              onClick={() => {
                setActiveFilter('active');
                setPage(1);
              }}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeFilter === 'active'
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => {
                setActiveFilter('inactive');
                setPage(1);
              }}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeFilter === 'inactive'
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Inactive
            </button>
            <button
              onClick={() => {
                setActiveFilter('lowStock');
                setPage(1);
              }}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeFilter === 'lowStock'
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Low Stock
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('itemNumber')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Item Number</span>
                    <SortIcon field="itemNumber" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('description')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Description</span>
                    <SortIcon field="description" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('price')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Price</span>
                    <SortIcon field="price" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {inventory.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    No inventory items found
                  </td>
                </tr>
              ) : (
                inventory.map((item) => {
                  const isLowStock = item.reorderPoint !== null && 
                    parseFloat(item.quantityOnHand) <= parseFloat(item.reorderPoint);
                  
                  return (
                    <tr 
                      key={item.id}
                      className={`hover:bg-gray-50 transition-colors duration-500 ${
                        highlightedItemNumber === item.itemNumber
                          ? 'bg-green-100 ring-2 ring-green-400 ring-inset'
                          : isLowStock ? 'bg-yellow-50' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {item.itemNumber}
                        </div>
                        {item.manufacturerPartNumber && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            MPN: {item.manufacturerPartNumber}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {item.description}
                        </div>
                        {item.purchaseDescription && item.purchaseDescription !== item.description && (
                          <div className="text-xs text-gray-500 mt-1">
                            Purchase: {item.purchaseDescription}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          ${parseFloat(item.price).toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          item.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {isLowStock && (
                          <AlertTriangle className="h-4 w-4 text-yellow-600 inline-block ml-2" title="Low Stock" />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewClick(item)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditClick(item)}
                            className="text-gray-600 hover:text-gray-900"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(item)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && (
          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700">Show:</label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                    setPage(1);
                  }}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={500}>500</option>
                </select>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={pagination.page <= 1}
                className="btn btn-secondary btn-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="btn btn-secondary btn-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals — details first so edit form stacks on top when both open */}
      <InventoryDetailsModal
        item={selectedItemForDetails}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedItemForDetails(null);
        }}
        onEdit={(item) => {
          setSelectedItem(item);
          setFormMode('edit');
          setIsFormModalOpen(true);
        }}
      />

      <InventoryFormModal
        item={selectedItem}
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setSelectedItem(null);
        }}
        onCreated={handleItemCreated}
        mode={formMode}
      />

      {/* Import Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Import Inventory from CSV</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select CSV File
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {importFile && (
                    <p className="mt-2 text-sm text-gray-600">
                      Selected: {importFile.name}
                    </p>
                  )}
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setIsImportModalOpen(false);
                      setImportFile(null);
                    }}
                    className="btn btn-secondary"
                    disabled={isImporting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImportSubmit}
                    className="btn btn-primary"
                    disabled={!importFile || isImporting}
                  >
                    {isImporting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Import
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
